import React, { useMemo } from 'react'
import { View, Text } from 'react-native'
import { GroupHeroBanner } from '../../../../components/worldcup/GroupHeroBanner/GroupHeroBanner'
import { Skeleton } from '../../../../components/ui/Skeleton'
import type { WorldCupGroup } from '../../../worldcup/types/worldcup.types'
import type { Theme } from '../../../theme/getTheme'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { getBrandingPreviewSectionStyles, swatchColorStyle } from './BrandingPreviewSection.styles'

interface BrandingPreviewSectionProps {
  primaryColor: string
  secondaryColor: string
  logoUrl?: string | null
  bannerUrl?: string | null
  orgName?: string | null
  isLoading?: boolean
  theme: Theme
}

export const BrandingPreviewSection: React.FC<BrandingPreviewSectionProps> = ({
  primaryColor,
  secondaryColor,
  logoUrl,
  bannerUrl,
  orgName,
  isLoading = false,
  theme,
}) => {
  const copy = useAccountAdminCopy()
  const previewCopy = copy.brandingPreview
  const styles = useMemo(() => getBrandingPreviewSectionStyles(theme), [theme])

  const previewGroup = useMemo(
    (): WorldCupGroup => ({
      id: 'preview',
      name: previewCopy.SQUAD_PLACEHOLDER_NAME,
      memberCount: 24,
      userRank: 1,
      userPoints: 0,
      inviteCode: null,
      companyBranding: {
        companyName: orgName ?? previewCopy.COMPANY_NAME_PLACEHOLDER,
        primaryColor,
        secondaryColor: secondaryColor ?? undefined,
        logoUrl: logoUrl ?? null,
        bannerUrl: bannerUrl ?? null,
      },
    }),
    [primaryColor, secondaryColor, logoUrl, bannerUrl, orgName, previewCopy],
  )

  const heroLabelOverrides = useMemo(
    () => ({
      back: previewCopy.HERO_BACK_LABEL,
      companyGroup: previewCopy.HERO_COMPANY_GROUP_LABEL,
      employees: previewCopy.HERO_EMPLOYEES_LABEL,
    }),
    [previewCopy],
  )

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{previewCopy.SECTION_LABEL}</Text>
      {isLoading ? (
        <Skeleton height={152} borderRadius={12} />
      ) : (
        /* pointerEvents="none" makes the banner purely visual — no tap/press interactions */
        <View style={styles.bannerWrapper} pointerEvents="none">
          <GroupHeroBanner
            group={previewGroup}
            onBack={() => {}}
            labelOverrides={heroLabelOverrides}
          />
        </View>
      )}
      <View style={styles.colorRow}>
        <View style={styles.colorChip}>
          <View style={[styles.colorSwatch, swatchColorStyle(primaryColor)]} />
          <Text style={styles.colorChipLabel}>{previewCopy.COLOR_PRIMARY_LABEL}</Text>
        </View>
        <View style={styles.colorChip}>
          <View style={[styles.colorSwatch, swatchColorStyle(secondaryColor)]} />
          <Text style={styles.colorChipLabel}>{previewCopy.COLOR_SECONDARY_LABEL}</Text>
        </View>
      </View>
    </View>
  )
}
